// Recording usage (spec/13 §6.4 Waste; `inventory:waste`, `inventory:adjust`; `inventory:supplies` for supplies):
// what left the shelf without being sold, in one dialog with four kinds —
//
//   Wasted       a reason (expired, damaged, spilled, quality, other), by item or by dish
//   Staff meal   by item or by dish
//   Issued       a supply put to use, optionally to a PC
//   Correction   one item, more or less, with the reason in words (`inventory:adjust`) — a count is the usual way
//
// "By dish" names a menu product with its size, extras and how many servings, and takes its recipe at this venue away.
// One `Idempotency-Key` is kept while the same entry is sent, so a retry never records it twice.

import type { CreateStockUsageRequest, MenuProduct, StockItem, StockItemPack, StockUsageKind, StockWasteReason } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { Button, Checkbox, Dialog, Input, SegmentedControl, SelectField, TextField, useToast } from '@clavian/ui-web';
import { useRef, useState, type FormEvent, type ReactNode } from 'react';

import { Icon } from '../../shell/nav-icons';
import { useCurrentVenue } from '../../shell/scope';
import { Notice } from '../../ui';
import { useMenuProducts } from '../menu/queries';
import { useStations } from '../../stations/queries';
import { suppliesRule, type StockAccess } from './access';
import { useStockFormat } from './format';
import { blankEntry, entryToWire, entryTotal, ItemPicker, QuantityFields, type QuantityEntry } from './parts';
import { useCreateAdjustment, useRecordUsage, useStockItems, useStockRecipes } from './queries';
import type { StockDialog } from './StockPage';
import { useProblem } from '../../api/use-problem';

export type UsageMode = StockUsageKind | 'adjust';
const REASONS: readonly StockWasteReason[] = ['expired', 'damaged', 'spilled', 'quality', 'other'];
const NO_STATION = 'none';

type Line =
  | { readonly key: string; readonly by: 'item'; readonly item: StockItem; readonly entry: QuantityEntry }
  | { readonly key: string; readonly by: 'dish'; readonly product: MenuProduct; readonly variantId: string | null; readonly modifierIds: readonly string[]; readonly servings: string };

let lineKey = 0;
const key = () => `usage-${(lineKey += 1)}`;

export function UsageDialog({ dialog, access, onClose }: { readonly dialog: Extract<StockDialog, { kind: 'usage' }> | null; readonly access: StockAccess; readonly onClose: () => void }): ReactNode {
  const { t } = useTranslation('console');
  return (
    <Dialog open={dialog !== null} onOpenChange={(open) => (open ? undefined : onClose())} size="lg" dirty="auto" title={t('stock.usage.title')} description={t('stock.usage.hint')}>
      {dialog !== null ? <UsageForm start={dialog} access={access} onDone={onClose} /> : null}
    </Dialog>
  );
}

function UsageForm({ start, access, onDone }: { readonly start: Extract<StockDialog, { kind: 'usage' }>; readonly access: StockAccess; readonly onDone: () => void }): ReactNode {
  const { t } = useTranslation('console');
  const problem = useProblem();
  const { show } = useToast();
  const venue = useCurrentVenue();
  const stock = useStockFormat();
  const items = useStockItems(venue.organizationId, venue.id);
  const recipes = useStockRecipes(venue.organizationId, venue.id, access.usage && !access.usageSuppliesOnly);
  const products = useMenuProducts(venue.organizationId);
  const usage = useRecordUsage(venue.organizationId, venue.id);
  const adjust = useCreateAdjustment(venue.organizationId, venue.id);

  const modes: UsageMode[] = [
    ...(access.usage ? (access.usageSuppliesOnly ? (['issue', 'waste'] as const) : (['waste', 'staff_meal', 'issue'] as const)) : []),
    ...(access.adjust ? (['adjust'] as const) : []),
  ];
  const [mode, setMode] = useState<UsageMode>(start.mode !== undefined && modes.includes(start.mode) ? start.mode : (modes[0] ?? 'waste'));
  const [reason, setReason] = useState<StockWasteReason | ''>('');
  const [note, setNote] = useState('');
  const [stationId, setStationId] = useState(NO_STATION);
  const [lines, setLines] = useState<readonly Line[]>(() => (start.item === undefined ? [] : [{ key: key(), by: 'item', item: start.item, entry: blankEntry(start.item) }]));
  // A correction: one item, added or taken away.
  const [direction, setDirection] = useState<'add' | 'remove'>('remove');
  const [errors, setErrors] = useState<Readonly<Record<string, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const attempt = useRef<{ body: string; key: string } | null>(null);

  const suppliesOnly = mode === 'adjust' ? access.adjustSuppliesOnly : access.usageSuppliesOnly;
  const itemLines = lines.filter((line): line is Extract<Line, { by: 'item' }> => line.by === 'item');
  const correction = mode === 'adjust' ? itemLines[0] : undefined;
  const dishes = (products.data ?? []).filter((product) => (recipes.data ?? []).some((recipe) => recipe.productId === product.id && recipe.modifierId === null));
  const update = (lineKeyValue: string, patch: Partial<Line>) => setLines((current) => current.map((line) => (line.key === lineKeyValue ? ({ ...line, ...patch } as Line) : line)));
  const pick = (item: StockItem, pack: StockItemPack | null) => {
    const line: Line = { key: key(), by: 'item', item, entry: blankEntry(item, pack) };
    setLines((current) => (mode === 'adjust' ? [line] : [...current, line]));
  };

  function key2(request: unknown): string {
    const text = JSON.stringify(request);
    if (attempt.current?.body !== text) attempt.current = { body: text, key: crypto.randomUUID() };
    return attempt.current.key;
  }

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: Record<string, string> = {};
    for (const line of mode === 'adjust' ? itemLines.slice(0, 1) : lines) {
      if (line.by === 'item') {
        const quantity = entryTotal(line.entry, line.item);
        if (quantity === null || quantity === 'invalid' || quantity <= 0) next[line.key] = t('stock.usage.quantityRequired');
      } else if (!/^\d+$/.test(line.servings.trim()) || Number(line.servings) < 1) next[line.key] = t('stock.usage.servingsRequired');
    }
    if (mode === 'waste' && reason === '') next['reason'] = t('stock.usage.reasonRequired');
    if (mode === 'adjust' && note.trim() === '') next['note'] = t('stock.usage.noteRequired');
    setErrors(next);
    const empty = mode === 'adjust' ? correction === undefined : lines.length === 0;
    setFormError(empty ? t('stock.usage.noLines') : null);
    if (empty || Object.keys(next).length > 0) return;

    if (mode === 'adjust' && correction !== undefined) {
      const quantity = entryTotal(correction.entry, correction.item) as number;
      const body = { stockItemId: correction.item.id, quantity: direction === 'remove' ? -quantity : quantity, note: note.trim() };
      adjust.mutate(
        { body, key: key2(body) },
        {
          onSuccess: () => {
            show({ tone: 'ok', title: t('stock.usage.adjusted', { name: correction.item.name }) });
            onDone();
          },
          onError: (failure) => setFormError(problem.text(failure, { fallback: t('stock.usage.refused') })),
        },
      );
      return;
    }
    const kind = mode as StockUsageKind;
    const body: CreateStockUsageRequest = {
      kind,
      ...(kind === 'waste' && reason !== '' ? { reason } : {}),
      ...(note.trim() === '' ? {} : { note: note.trim() }),
      ...(kind === 'issue' && stationId !== NO_STATION ? { stationId } : {}),
      lines: lines.map((line) =>
        line.by === 'item'
          ? { stockItemId: line.item.id, ...entryToWire(line.entry, line.item) }
          : {
              productId: line.product.id,
              ...(line.variantId === null ? {} : { variantId: line.variantId }),
              ...(line.modifierIds.length === 0 ? {} : { modifierIds: [...line.modifierIds] }),
              servings: Number(line.servings),
            },
      ),
    };
    usage.mutate(
      { body, key: key2(body) },
      {
        onSuccess: (result) => {
          show({ tone: 'ok', title: t(`stock.usage.done.${kind}`, { count: result.movements.length }) });
          onDone();
        },
        onError: (failure) => setFormError(problem.text(failure, { fallback: t('stock.usage.refused') })),
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('stock.usage.title')} noValidate onSubmit={submit}>
      {formError !== null ? (
        <Notice tone="danger" role="alert">
          {formError}
        </Notice>
      ) : null}
      <div className="console-field-block">
        <SegmentedControl<UsageMode>
          label={t('stock.usage.kind')}
          value={mode}
          onValueChange={(next) => {
            setMode(next);
            setErrors({});
            if (next === 'adjust') setLines((current) => current.filter((line) => line.by === 'item').slice(0, 1));
          }}
          options={modes.map((one) => ({ value: one, label: t(`stock.usage.mode.${one}`) }))}
        />
        <p className="console-subtle">{t(`stock.usage.modeHint.${mode}`)}</p>
      </div>

      {mode === 'waste' ? (
        <SelectField
          label={t('stock.usage.reason')}
          required
          placeholder={t('stock.usage.reasonPlaceholder')}
          value={reason}
          onValueChange={(value) => setReason(value as StockWasteReason)}
          options={REASONS.map((one) => ({ value: one, label: t(`stock.wasteReason.${one}`) }))}
          error={errors['reason']}
        />
      ) : null}
      {mode === 'issue' ? <StationSelect value={stationId} onChange={setStationId} /> : null}

      {mode === 'adjust' ? (
        correction === undefined ? (
          <ItemPicker organizationId={venue.organizationId} venueId={venue.id} items={items.data ?? []} label={t('stock.usage.pickItem')} onPick={pick} autoFocus {...(suppliesOnly ? { allow: suppliesRule(true) } : {})} />
        ) : (
          <div className="console-stock-line">
            <div className="console-stock-line__head">
              <span className="console-stock-line__name">{correction.item.name}</span>
              <span className="console-subtle">{t('stock.receive.onHandNow', { quantity: stock.quantity(correction.item.onHand, correction.item) })}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => setLines([])}>
                {t('stock.usage.change')}
              </Button>
            </div>
            <SegmentedControl<'add' | 'remove'>
              size="sm"
              label={t('stock.usage.direction')}
              value={direction}
              onValueChange={setDirection}
              options={[
                { value: 'remove', label: t('stock.usage.remove') },
                { value: 'add', label: t('stock.usage.add') },
              ]}
            />
            <QuantityFields item={correction.item} entry={correction.entry} label={correction.item.name} onChange={(entry) => update(correction.key, { entry })} error={errors[correction.key]} />
            <AfterLine item={correction.item} change={(() => {
              const quantity = entryTotal(correction.entry, correction.item);
              return typeof quantity === 'number' ? (direction === 'remove' ? -quantity : quantity) : null;
            })()} />
          </div>
        )
      ) : (
        <>
          <ItemPicker
            organizationId={venue.organizationId}
            venueId={venue.id}
            items={items.data ?? []}
            label={t('stock.usage.addItem')}
            exclude={new Set(itemLines.map((line) => line.item.id))}
            onPick={pick}
            autoFocus={start.item === undefined}
            {...(suppliesOnly ? { allow: suppliesRule(true) } : {})}
          />
          {dishes.length > 0 && mode !== 'issue' ? (
            <SelectField
              label={t('stock.usage.addDish')}
              placeholder={t('stock.usage.addDishPlaceholder')}
              value=""
              onValueChange={(productId) => {
                const product = dishes.find((one) => one.id === productId);
                if (product !== undefined) setLines((current) => [...current, { key: key(), by: 'dish', product, variantId: product.variants[0]?.id ?? null, modifierIds: [], servings: '1' }]);
              }}
              options={dishes.map((product) => ({ value: product.id, label: product.name }))}
            />
          ) : null}
          {lines.length === 0 ? (
            <p className="console-stock-hint">{t('stock.usage.empty')}</p>
          ) : (
            <ol className="console-stock-lines" aria-label={t('stock.usage.linesLabel')}>
              {lines.map((line) => (
                <li key={line.key} className="console-stock-line">
                  <div className="console-stock-line__head">
                    <span className="console-stock-line__name">{line.by === 'item' ? line.item.name : line.product.name}</span>
                    <span className="console-subtle">{line.by === 'item' ? t('stock.receive.onHandNow', { quantity: stock.quantity(line.item.onHand, line.item) }) : t('stock.usage.byRecipe')}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      icon={<Icon name="close" />}
                      label={t('stock.receive.removeLine', { name: line.by === 'item' ? line.item.name : line.product.name })}
                      onClick={() => setLines((current) => current.filter((one) => one.key !== line.key))}
                    />
                  </div>
                  {line.by === 'item' ? (
                    <QuantityFields item={line.item} entry={line.entry} label={line.item.name} onChange={(entry) => update(line.key, { entry })} error={errors[line.key]} />
                  ) : (
                    <DishFields line={line} onChange={(patch) => update(line.key, patch)} error={errors[line.key]} />
                  )}
                </li>
              ))}
            </ol>
          )}
        </>
      )}

      <TextField
        label={mode === 'adjust' ? t('stock.usage.why') : t('stock.usage.note')}
        required={mode === 'adjust'}
        showOptional={mode !== 'adjust'}
        value={note}
        placeholder={mode === 'adjust' ? t('stock.usage.whyPlaceholder') : undefined}
        onChange={(event) => setNote(event.target.value)}
        error={errors['note']}
      />
      <div className="console-form__actions console-form__actions--end">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant={mode === 'waste' ? 'danger' : 'primary'} busy={usage.isPending || adjust.isPending}>
          {t(`stock.usage.submit.${mode}`)}
        </Button>
      </div>
    </form>
  );
}

/** "On hand after: 2.9 kg" under a correction, so the change is checked before it is saved. */
function AfterLine({ item, change }: { readonly item: StockItem; readonly change: number | null }): ReactNode {
  const { t } = useTranslation('console');
  const stock = useStockFormat();
  if (change === null) return null;
  return <p className="console-subtle console-num">{t('stock.usage.after', { quantity: stock.quantity(item.onHand + change, item) })}</p>;
}

/** A dish by its size, extras and servings: what its recipe at this venue takes away. */
function DishFields({ line, onChange, error }: { readonly line: Extract<Line, { by: 'dish' }>; readonly onChange: (patch: Partial<Line>) => void; readonly error?: string | undefined }): ReactNode {
  const { t } = useTranslation('console');
  const modifiers = line.product.modifierGroups.flatMap((group) => group.modifiers);
  return (
    <div className="console-stock-dish">
      {line.product.variants.length > 0 ? (
        <SelectField
          label={t('stock.usage.size')}
          value={line.variantId ?? ''}
          onValueChange={(variantId) => onChange({ variantId })}
          options={line.product.variants.map((variant) => ({ value: variant.id, label: variant.name }))}
        />
      ) : null}
      {modifiers.length > 0 ? (
        <fieldset className="console-stock-dish__extras">
          <legend className="clav-field__label">{t('stock.usage.extras')}</legend>
          {modifiers.map((modifier) => (
            <Checkbox
              key={modifier.id}
              label={modifier.name}
              checked={line.modifierIds.includes(modifier.id)}
              onCheckedChange={(checked) => onChange({ modifierIds: checked === true ? [...line.modifierIds, modifier.id] : line.modifierIds.filter((one) => one !== modifier.id) })}
            />
          ))}
        </fieldset>
      ) : null}
      <label className="console-stock-dish__servings">
        <span className="clav-field__label">{t('stock.usage.servings')}</span>
        <Input numeric inputMode="numeric" value={line.servings} aria-invalid={error === undefined ? undefined : true} onChange={(event) => onChange({ servings: event.target.value })} />
      </label>
      {error !== undefined ? (
        <p className="clav-field__error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** The PC a supply went to, optional. Only read when issuing. */
function StationSelect({ value, onChange }: { readonly value: string; readonly onChange: (value: string) => void }): ReactNode {
  const { t } = useTranslation('console');
  const venue = useCurrentVenue();
  const stations = useStations(venue.organizationId, venue.id);
  const list = (stations.data?.pages ?? []).flatMap((page) => page.data);
  return (
    <SelectField
      label={t('stock.usage.station')}
      value={value}
      onValueChange={onChange}
      options={[
        { value: NO_STATION, label: t('stock.usage.noStation') },
        ...list.map((station) => ({ value: station.id, label: station.name === null ? t('stock.usage.stationNumber', { number: station.number }) : `${t('stock.usage.stationNumber', { number: station.number })} · ${station.name}` })),
      ]}
    />
  );
}
